import { Col, Label, Row } from "reactstrap";
import React, { useEffect, useMemo, useState } from "react";
import Select from "../../Common/AppSelect";
import { isEmpty } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { getCompanyToUserListTeam } from "../../../store/companyToUser/action";

// Change #100477 - the dashboard, menu and landing page are back on this step
// (Task 24512 had moved them to Staff Details), so "copy from an existing
// user" can fill them AND the security roles in one go. They are still
// company-local Staff settings; the templates they point at live in the
// company database, so a person can have a different dashboard and menu in
// each company they belong to.
const TabDefaultOptions = ({
  createUpdateData,
  setCreateUpdateData,
  activeVerticalTab,
  setActiveVerticalTab,
  isEdit,
}) => {
  const { userAvailableDashboardAPI } = useSelector((state) => ({
    userAvailableDashboardAPI:
      state.CompanyToUser.userAvailableDashboard?.dashboardtemplate_list,
  }));
  const { menuNavigationListAPI } = useSelector((state) => ({
    menuNavigationListAPI:
      state.NavigationMenu.menuNavigationList?.menunav_list,
  }));
  const { availableMenuNavigationItemsAPI } = useSelector((state) => ({
    // Task 24678 - MenuNavOption (master), NOT MenuNavItem (per company).
    // They are different tables with different id sequences, and
    // AuthLoginController resolves Staff.DefaultOpeningNavMenu against
    // MenuNavOption.Id - so a MenuNavItem id saved here points at whichever
    // MenuNavOption happens to share the number, or at nothing.
    availableMenuNavigationItemsAPI:
      state.NavigationMenu.availableMenuNavigationItems?.menunavoption_list,
  }));
  const { securityAuthRoleListAPI } = useSelector((state) => ({
    securityAuthRoleListAPI:
      state.Security.securityAuthRoleList?.securityauthrole_list,
  }));

  // Change #100472 - "set them up like Amy": pick an existing user and their
  // dashboard, menu and landing page are copied into the fields below, which
  // stay editable. The Users list already carries the Staff values login
  // reads (Staff_*), with the CompanyToUser columns as a fallback.
  // Change #100477 - their security roles are copied too.
  const dispatch = useDispatch();
  const { teamListAPI } = useSelector((state) => ({
    teamListAPI: state.CompanyToUser.getCompanyToUserListTeam?.companytouser_list,
  }));
  useEffect(() => {
    if (!teamListAPI) dispatch(getCompanyToUserListTeam());
  }, []);

  const [referenceUserId, setReferenceUserId] = useState(null);
  const referenceOf = (u) => ({
    DashboardTemplate_Id: u?.Staff_DashboardTemplate_Id || u?.DashboardTemplate_Id || null,
    MenuNav_Id: u?.Staff_MenuNav_Id || u?.MenuNav_Id || null,
    DefaultOpeningNavMenu: u?.Staff_DefaultOpeningNavMenu || null,
  });
  // Only roles that still exist in this company - the list endpoint left
  // joins SecurityAuthRole, so a deleted role comes back with a null Id.
  const referenceRolesOf = (u) =>
    (u?.securityauthrole_list || [])
      .map((r) => r?.SecurityAuthRole_Id)
      .filter(
        (id) => id && (securityAuthRoleListAPI || []).some((role) => role?.Id === id)
      )
      .filter((id, i, all) => all.indexOf(id) === i)
      .map((id) => ({ Id: id }));
  const referenceOptions = useMemo(
    () =>
      (teamListAPI || [])
        // Disabled staff are not a template anyone wants to copy, and a user
        // with nothing set has nothing to give.
        .filter((u) => u?.Staff_FlagActive !== "0")
        .filter(
          (u) =>
            Object.values(referenceOf(u)).some(Boolean) ||
            referenceRolesOf(u).length > 0
        )
        .map((u) => ({
          value: u?.User_Id,
          label:
            u?.User_UserFullName ||
            `${u?.User_UserFirstName || ""} ${u?.User_UserLastName || ""}`.trim() ||
            u?.User_Email,
          searchText: `${u?.User_UserFullName || ""} ${u?.User_Email || ""}`,
          user: u,
        }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label))),
    [teamListAPI, securityAuthRoleListAPI]
  );

  const applyReferenceUser = (option) => {
    setReferenceUserId(option?.value || null);
    if (!option) return;
    const ref = referenceOf(option.user);
    // Only overwrite what the reference user actually has.
    const patch = {};
    Object.keys(ref).forEach((k) => {
      if (ref[k]) patch[k] = ref[k];
    });
    const roles = referenceRolesOf(option.user);
    if (roles.length > 0) patch.securityauthrole_list = roles;
    setCreateUpdateData({ ...createUpdateData, ...patch });
  };

  return (
    <Row>
      <div>
        <h2>Default Options</h2>
        <p className="text-muted mb-0">
          Their dashboard, menu, landing page and security roles.
        </p>
      </div>
      <hr />
      <Col lg={12} className={"p-2"}>
        <Label>Copy from an existing user</Label>
        <Select
          isClearable
          placeholder="Pick a user to copy their dashboard, menu, landing page and security roles"
          options={referenceOptions}
          value={
            referenceOptions.find((o) => o.value === referenceUserId) || null
          }
          onChange={applyReferenceUser}
          filterOption={(option, input) =>
            option?.data?.searchText
              ?.toLowerCase()
              ?.includes((input || "").toLowerCase())
          }
        />
        <small className="text-muted">
          Fills in the four fields below. You can still change any of them.
        </small>
      </Col>
      <Col lg={12} className={"p-2"}>
        <Label>
          Dashboard Template <span className={"text-danger"}>*</span>
        </Label>
        <Select
          options={userAvailableDashboardAPI?.map(
            (item) =>
              item?.Name && {
                value: item?.Id,
                label: item?.Name,
              }
          )}
          value={
            createUpdateData?.DashboardTemplate_Id
              ? {
                  value: createUpdateData?.DashboardTemplate_Id,
                  label: userAvailableDashboardAPI?.find(
                    (item) =>
                      item?.Id === createUpdateData?.DashboardTemplate_Id
                  )?.Name,
                }
              : []
          }
          onChange={(e) => {
            setCreateUpdateData({
              ...createUpdateData,
              DashboardTemplate_Id: e.value,
            });
          }}
        ></Select>
      </Col>
      <Col lg={12} className={"p-2"}>
        <Label>
          Select Menu Template <span className={"text-danger"}>*</span>
        </Label>
        <Select
          options={menuNavigationListAPI?.map((item) => ({
            value: item?.Id,
            label: item?.Name,
          }))}
          value={
            createUpdateData?.MenuNav_Id
              ? {
                  value: createUpdateData?.MenuNav_Id,
                  label: menuNavigationListAPI?.find(
                    (item) => item?.Id === createUpdateData?.MenuNav_Id
                  )?.Name,
                }
              : []
          }
          onChange={(e) => {
            setCreateUpdateData({
              ...createUpdateData,
              MenuNav_Id: e.value,
            });
          }}
        ></Select>
      </Col>
      <Col lg={12} className={"p-2"}>
        <Label>
          Select Default Landing Page <span className={"text-danger"}>*</span>
        </Label>
        <Select
          options={availableMenuNavigationItemsAPI?.map((item) => ({
            value: item?.Id,
            label: item?.Name,
          }))}
          value={
            createUpdateData?.DefaultOpeningNavMenu
              ? {
                  value: createUpdateData?.DefaultOpeningNavMenu,
                  label: availableMenuNavigationItemsAPI?.find(
                    (item) =>
                      item?.Id === createUpdateData?.DefaultOpeningNavMenu
                  )?.Name,
                }
              : []
          }
          onChange={(e) => {
            setCreateUpdateData({
              ...createUpdateData,
              DefaultOpeningNavMenu: e.value,
            });
          }}
        ></Select>
      </Col>
      <Col lg={12} className={"p-2"}>
        <Label>
          Security Role <span className={"text-danger"}>*</span>
        </Label>
        <Select
          isMulti={true}
          options={securityAuthRoleListAPI?.map(
            (item) =>
              item?.Name && {
                value: item?.Id,
                label: (
                  <span className={"d-flex align-items-center"}>
                    <span className={"me-1"}>{item?.Name}</span>
                    {item?.MasterSecurityAuthRole_Id !== null ? (
                      <span
                        className={"badge badge-soft-success"}
                        title={"Global Setting"}
                      >
                        STANDARD
                      </span>
                    ) : (
                      <span
                        className={"badge badge-soft-dark"}
                        title={"Local Setting"}
                      >
                        CUSTOM
                      </span>
                    )}{" "}
                  </span>
                ),
              }
          )}
          value={
            isEmpty(createUpdateData?.securityauthrole_list)
              ? []
              : createUpdateData?.securityauthrole_list?.map((item) => {
                  return {
                    value: item?.Id,
                    label: securityAuthRoleListAPI?.find(
                      (role) => role?.Id === item?.Id
                    )?.Name,
                  };
                })
          }
          onChange={(e) => {
            setCreateUpdateData({
              ...createUpdateData,
              securityauthrole_list: e.map((item) => {
                return {
                  Id: item.value,
                };
              }),
            });
          }}
        ></Select>
      </Col>
      <Col lg={12} className={"p-2"}>
        <div className={"d-flex justify-content-between mt-2 "}>
          <button
            type="button"
            className={"btn btn-primary"}
            onClick={() => {
              setActiveVerticalTab(activeVerticalTab - 1);
            }}
          >
            <i className="ri-arrow-left-line align-bottom me-1" />
            Back
          </button>
          <button
            type="button"
            className={"btn btn-success"}
            onClick={() => {
              setActiveVerticalTab(activeVerticalTab + 1);
            }}
          >
            Next
            <i className="ri-arrow-right-line align-bottom ms-1" />
          </button>
        </div>
      </Col>
    </Row>
  );
};

export default TabDefaultOptions;
